(function () {
  'use strict';

  function parseProfile(root) {
    const registry = window.SmagtronCalculatorRegistry.createRegistry(window.SmagtronCalculatorRegistry.catalog);
    const resolved = registry.resolve(
      { profileId: root.dataset.profileId },
      { allowedStatuses: ['pilot'] }
    );
    if (!resolved.ok) throw new Error(resolved.reason);
    const runtime = JSON.parse(root.querySelector('[data-calculator-runtime]').textContent);
    const runtimeFormula = Object.fromEntries(
      Object.entries(runtime.formula || {}).filter(([, value]) => value !== null)
    );
    const profile = {
      ...resolved.profile,
      ...runtime,
      formula: { ...resolved.profile.formula, ...runtimeFormula }
    };
    if (runtime.optionPrices && profile.choices) {
      profile.choices = profile.choices.map((choice) => ({
        ...choice,
        values: choice.values.map((value) => ({
          ...value,
          priceCents: choice.key === 'graphicsService' && value.key === 'expert' && Number.isInteger(runtime.expertServiceCents)
            ? runtime.expertServiceCents
            : runtime.optionPrices?.[choice.key]?.[value.key] ?? value.priceCents
        }))
      }));
    }
    return profile;
  }

  function selection(root, profile) {
    const selected = {
      width: root.querySelector('[data-width]').value,
      height: root.querySelector('[data-height]').value
    };
    if (profile.upload) selected[profile.upload.key] = root.querySelector('[data-upload]')?.files?.[0] || null;
    for (const option of profile.choices || []) {
      selected[option.key] = root.querySelector(`[name="smagtron-${option.key}"]:checked`)?.value || '';
    }
    return selected;
  }

  function setError(root, key, message) {
    const node = root.querySelector(`[data-error-for="${key}"]`);
    if (!node) return;
    node.textContent = message || '';
    node.hidden = !message;
    if (key === 'width' || key === 'height') {
      root.querySelector(`[data-${key}]`).setAttribute('aria-invalid', String(Boolean(message)));
    } else if (key === root._smagtronProfile.upload?.key) {
      root.querySelector('[data-upload]').setAttribute('aria-invalid', String(Boolean(message)));
    } else {
      root.querySelectorAll(`[name="smagtron-${key}"]`).forEach((input) => {
        input.setAttribute('aria-invalid', String(Boolean(message)));
      });
    }
  }

  function render(root, profile) {
    const selected = selection(root, profile);
    if (!root._smagtronSelection || Object.keys(selected).some((key) => selected[key] !== root._smagtronSelection[key])) {
      root._smagtronGeneration = (root._smagtronGeneration || 0) + 1;
      root.querySelector('[data-status]').hidden = true;
    }
    root._smagtronSelection = selected;
    const result = window.SmagtronCalculatorCore.calculate(profile, selected);
    root.querySelector('[data-total]').textContent = result.totalCents
      ? window.SmagtronCalculatorCore.money(result.totalCents, profile.currency)
      : '–';
    root.querySelector('[data-area]').textContent = result.totalCents
      ? `${((Number(selected.width) * Number(selected.height)) / 1000000).toFixed(2).replace('.', ',')} m²`
      : '–';
    for (const key of ['width', 'height', '_price', profile.upload?.key, ...(profile.choices || []).map((option) => option.key)].filter(Boolean)) {
      setError(root, key, result.errors[key]);
    }
    const submit = root.querySelector('[data-submit]');
    submit.disabled = Boolean(root._smagtronOperation) || !result.valid;
    root._smagtronResult = result;
    return result;
  }

  async function requestQuote(input, fetchFn) {
    const quoteSelection = input.selection || {
      widthMm: input.widthMm,
      heightMm: input.heightMm
    };
    const response = await fetchFn('/apps/smagtron-calculator/quote', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        productId: input.productId,
        profileId: input.profileId,
        selection: quoteSelection
      })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || !payload.ok) {
      throw new Error(payload.error || `Preisprüfung fehlgeschlagen (HTTP ${response.status})`);
    }
    return payload;
  }

  function isCurrentOperation(root, operation) {
    const selected = selection(root, root._smagtronProfile);
    return root._smagtronOperation === operation && root._smagtronGeneration === operation.generation &&
      Object.keys(selected).every((key) => selected[key] === operation.selected[key]);
  }

  async function addToCart(root) {
    if (root._smagtronOperation) return;
    const result = root._smagtronResult;
    if (!result || !result.valid) return;
    const submit = root.querySelector('[data-submit]');
    const status = root.querySelector('[data-status]');
    const profile = root._smagtronProfile;
    const selected = Object.freeze(selection(root, profile));
    const operation = { generation: root._smagtronGeneration, selected, redirecting: false };
    root._smagtronOperation = operation;
    submit.disabled = true;
    submit.textContent = 'Wird hinzugefügt …';
    status.hidden = true;
    try {
      const quoteSelection = {
        widthMm: Number(selected.width),
        heightMm: Number(selected.height)
      };
      for (const option of profile.choices || []) quoteSelection[option.key] = selected[option.key];
      if (profile.upload) quoteSelection.uploadName = selected[profile.upload.key]?.name || '';
      const quote = await requestQuote({
        productId: root.dataset.productId,
        profileId: profile.id,
        selection: quoteSelection
      }, window.fetch.bind(window));
      if (!isCurrentOperation(root, operation)) return;
      root.querySelector('[data-total]').textContent = window.SmagtronCalculatorCore.money(
        quote.priceCents,
        quote.currency
      );
      const properties = {
        ...quote.properties,
        _Calculator: `Smagtron ${profile.id}`
      };
      let cartOptions;
      if (profile.upload) {
        const formData = new FormData();
        formData.append('id', String(quote.variantId));
        formData.append('quantity', '1');
        for (const [key, value] of Object.entries(properties)) formData.append(`properties[${key}]`, String(value));
        formData.set(`properties[${profile.upload.property}]`, selected[profile.upload.key]);
        cartOptions = { method: 'POST', headers: { Accept: 'application/json' }, body: formData };
      } else {
        cartOptions = {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(quote.variantId), quantity: 1, properties }] })
        };
      }
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, cartOptions);
      if (!isCurrentOperation(root, operation)) return;
      if (!response.ok) throw new Error(`Shopify meldet HTTP ${response.status}`);
      operation.redirecting = true;
      window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
    } catch (error) {
      operation.redirecting = false;
      if (isCurrentOperation(root, operation)) {
        status.textContent = `Hinzufügen nicht möglich: ${error.message}`;
        status.hidden = false;
      }
    } finally {
      if (root._smagtronOperation === operation && !operation.redirecting) {
        root._smagtronOperation = null;
        submit.textContent = 'In den Warenkorb';
        render(root, profile);
      }
    }
  }

  function init(root) {
    root.closest('product-info')?.classList.add('smagtron-product-info--calculator');
    let profile;
    try { profile = parseProfile(root); }
    catch {
      root.querySelector('[data-status]').textContent = 'Calculator-Konfiguration konnte nicht geladen werden.';
      root.querySelector('[data-status]').hidden = false;
      return;
    }
    root._smagtronProfile = profile;
    root.querySelectorAll('[data-width], [data-height], [data-upload], [data-profile-choice]').forEach((input) => {
      input.addEventListener('input', () => render(root, profile));
      input.addEventListener('change', () => render(root, profile));
    });
    root.querySelector('form').addEventListener('submit', (event) => {
      event.preventDefault();
      const result = render(root, profile);
      if (result.valid) addToCart(root);
    });
    render(root, profile);
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-smagtron-area-calculator]').forEach(init);
  });

  if (typeof module === 'object' && module.exports) {
    module.exports = { requestQuote };
  }
})();
